import React, { useState, useRef, useEffect } from 'react';
import { Users, MessageCircle, Circle, Phone, ChevronDown, ChevronRight } from 'lucide-react';
import { useOnlineUsers } from '../hooks/useOnlineUsers';
import { useMessaging } from '../hooks/useMessaging';
import { useAuth } from '../contexts/AuthContext';
import MessageModal from './MessageModal';
import GroupChatModal from './GroupChatModal';

const API_BASE_URL = import.meta.env.VITE_API_URL;

interface OnlineUsersWidgetProps {
  onStartVideoCall?: (userId: number | null, callType: 'audio' | 'video', callId: number, roomName: string) => void;
}

const OnlineUsersWidget: React.FC<OnlineUsersWidgetProps> = React.memo(({ onStartVideoCall }) => {
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showGroupChat, setShowGroupChat] = useState(false);
  const [groupUnreadCount, setGroupUnreadCount] = useState(0);
  const [missedCallCount, setMissedCallCount] = useState(0);
  const [offlineCollapsed, setOfflineCollapsed] = useState(true);
  const widgetRef = useRef<HTMLDivElement>(null);
  const { onlineUsers, loading } = useOnlineUsers();
  const { unreadCounts } = useMessaging();

  // Initialize group chat sound only for authorized roles
  const isAuthorized = user && ['admin', 'superadmin', 'teknisyen'].includes(user.role);

  // Admin, superadmin ve teknisyen kullanıcıları chat penceresini görebilir
  if (!isAuthorized) {
    return null;
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load group chat unread count
  const loadGroupUnreadCount = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/group-chat/unread-count`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        if (response.status === 429) {
          console.warn('Rate limit reached for group unread count');
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const text = await response.text();
      if (!text) {
        setGroupUnreadCount(0);
        return;
      }

      try {
        const data = JSON.parse(text);
        setGroupUnreadCount(data.count || 0);
      } catch (jsonError) {
        console.error('Invalid JSON response for group unread count:', text);
        setGroupUnreadCount(0);
      }
    } catch (error) {
      console.error('Error loading group unread count:', error);
      setGroupUnreadCount(0);
    }
  };

  // Load missed calls count
  const loadMissedCallCount = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/calls/missed-count`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        if (response.status === 429) {
          console.warn('Rate limit reached for missed calls count');
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setMissedCallCount(data.count || 0);
    } catch (error) {
      console.error('Error loading missed calls count:', error);
      setMissedCallCount(0);
    }
  };

  const formatLastSeen = (lastActivity?: string): string => {
    if (!lastActivity) return 'Bilinmiyor';
    const date = new Date(lastActivity);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Az önce';
    if (diffMins < 60) return `${diffMins} dk önce`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} saat önce`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} gün önce`;
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  useEffect(() => {
    loadGroupUnreadCount();
    loadMissedCallCount();
    // Auto-refresh counts every 10 seconds for better responsiveness
    const groupInterval = setInterval(loadGroupUnreadCount, 10000);
    const missedCallInterval = setInterval(loadMissedCallCount, 10000);
    
    // Login sonrası SweetAlert'ten chat widget açma
    const handleOpenChatWidget = () => {
      setIsExpanded(true);
    };

    // Grup sohbet açma event listener'ı
    const handleOpenGroupChat = (event: CustomEvent) => {
      const { messageId } = event.detail;
      setShowGroupChat(true);
      setIsExpanded(false);
      // MessageId'yi URL'ye ekle
      if (messageId) {
        const url = new URL(window.location.href);
        url.searchParams.set('messageId', messageId.toString());
        window.history.replaceState({}, '', url.toString());
      }
    };
    
    // Group chat read event listener'ı - unread count'u hemen güncelle
    const handleGroupChatRead = () => {
      setGroupUnreadCount(0);
      // 1 saniye sonra da gerçek count'u kontrol et
      setTimeout(() => {
        loadGroupUnreadCount();
      }, 1000);
    };

    // Group chat unread count refresh event listener
    const handleRefreshGroupUnreadCount = () => {
      loadGroupUnreadCount();
    };

    window.addEventListener('openChatWidget', handleOpenChatWidget);
    window.addEventListener('openGroupChat', handleOpenGroupChat as EventListener);
    window.addEventListener('groupChatRead', handleGroupChatRead);
    window.addEventListener('refreshGroupUnreadCount', handleRefreshGroupUnreadCount);

    return () => {
      clearInterval(groupInterval);
      clearInterval(missedCallInterval);
      window.removeEventListener('openChatWidget', handleOpenChatWidget);
      window.removeEventListener('openGroupChat', handleOpenGroupChat as EventListener);
      window.removeEventListener('groupChatRead', handleGroupChatRead);
      window.removeEventListener('refreshGroupUnreadCount', handleRefreshGroupUnreadCount);
    };
  }, []);

  const handleUserClick = (userId: number) => {
    setSelectedUserId(userId);
    setShowMessageModal(true);
    setIsExpanded(false);
  };

  const totalUnreadMessages = Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);

  // Offline admin/superadmin kullanıcıları
  const offlineAdminUsers = onlineUsers.filter(
    (u) => !u.is_online && ['admin', 'superadmin'].includes(u.role)
  );

  return (
    <>
      <div
        ref={widgetRef}
        className="fixed bottom-4 right-4 z-[9999] hidden md:block"
      >
        {/* Expanded Panel */}
        {isExpanded && (
          <div className="absolute bottom-16 right-0 w-80 md:w-80 sm:w-72 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 mb-2 max-w-[calc(100vw-2rem)] z-[10000]">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
Çevrimiçi Kullanıcılar ({onlineUsers.filter(u => u.is_online).length})
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {totalUnreadMessages + groupUnreadCount > 0 ? `${totalUnreadMessages + groupUnreadCount} okunmamış mesaj` : 'Tüm mesajlar okundu'}
              </p>
            </div>
            
            {/* Group Chat Button */}
            <div className="p-2 border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowGroupChat(true);
                  setIsExpanded(false);
                  // Modal açılırken unread count'u hemen sıfırla
                  setGroupUnreadCount(0);
                }}
                className={`w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-primary/10 dark:hover:bg-primary/20 cursor-pointer transition-colors bg-primary/10 dark:bg-primary/10 ${
                  groupUnreadCount > 0 ? 'animate-pulse' : ''
                }`}
              >
                <div className="relative">
                  <div className="w-10 h-10 bg-primary/20 dark:bg-primary/90 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-primary dark:text-primary" />
                  </div>
                  {groupUnreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold animate-bounce">
                      {groupUnreadCount}
                    </span>
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-primary-dark dark:text-accent">
                    Ekip Sohbeti
                  </p>
                  <p className="text-xs text-primary dark:text-primary">
                    Tüm ekip üyeleri
                  </p>
                </div>
                
                <MessageCircle className="w-4 h-4 text-primary dark:text-primary" />
              </button>
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                </div>
              ) : onlineUsers.filter(u => u.is_online).length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  Çevrimiçi kullanıcı yok
                </div>
              ) : (
                <div className="p-2">
                  {onlineUsers.filter(u => u.is_online).map((user) => {
                    const unreadCount = unreadCounts[user.id] || 0;
                    return (
                      <div
                        key={user.id}
                        onClick={() => handleUserClick(user.id)}
                        className={`flex items-center space-x-3 p-3 md:p-3 sm:p-4 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 cursor-pointer transition-colors touch-manipulation ${
                          unreadCount > 0 ? 'animate-pulse' : ''
                        }`}
                      >
                        <div className="relative">
                          <img
                            className="w-10 h-10 rounded-full"
                            src={user.avatar || 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'}
                            alt={user.name}
                          />
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800 bg-green-500"></div>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium truncate text-gray-900 dark:text-gray-100">
                              {user.name}
                            </p>
                            {unreadCount > 0 && (
                              <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full animate-bounce">
                                {unreadCount}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                              {user.role}
                            </p>
                            <span className="text-xs text-green-600 dark:text-green-400">
                              Çevrimiçi
                            </span>
                          </div>
                        </div>

                        <MessageCircle className="w-4 h-4 text-gray-400" />
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Çevrimdışı admin/superadmin kullanıcıları */}
              {offlineAdminUsers.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setOfflineCollapsed(!offlineCollapsed)}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <span className="flex items-center space-x-1.5">
                      {offlineCollapsed ? (
                        <ChevronRight className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5" />
                      )}
                      <span>Çevrimdışı Kullanıcılar ({offlineAdminUsers.length})</span>
                    </span>
                  </button>

                  {!offlineCollapsed && (
                    <div className="p-2 pt-0">
                      {offlineAdminUsers.map((offlineUser) => {
                        const unreadCount = unreadCounts[offlineUser.id] || 0;
                        return (
                          <div
                            key={offlineUser.id}
                            onClick={() => handleUserClick(offlineUser.id)}
                            className={`flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors opacity-70 ${
                              unreadCount > 0 ? 'opacity-100 animate-pulse' : ''
                            }`}
                          >
                            <div className="relative">
                              <img
                                className="w-9 h-9 rounded-full grayscale"
                                src={offlineUser.avatar || 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'}
                                alt={offlineUser.name}
                              />
                              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800 bg-gray-400"></div>
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium truncate text-gray-500 dark:text-gray-400">
                                  {offlineUser.name}
                                </p>
                                {unreadCount > 0 && (
                                  <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full animate-bounce">
                                    {unreadCount}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                Son çevrimiçi: {formatLastSeen(offlineUser.last_activity)}
                              </p>
                            </div>

                            <MessageCircle className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Widget Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`relative bg-primary hover:bg-primary-dark text-white rounded-full p-4 md:p-3 shadow-lg transition-all duration-300 min-w-[56px] min-h-[56px] md:min-w-[48px] md:min-h-[48px] flex items-center justify-center touch-manipulation ${
            (totalUnreadMessages > 0 || groupUnreadCount > 0) ? 'animate-pulse shadow-red-300/50 shadow-2xl ring-2 ring-red-400/50' : ''
          }`}
        >
          <Users className="w-7 h-7 md:w-6 md:h-6" />

        {/* Online user count badge */}
        {onlineUsers.filter(u => u.is_online).length > 0 && (
          <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs rounded-full w-7 h-7 md:w-6 md:h-6 flex items-center justify-center font-bold">
            {onlineUsers.filter(u => u.is_online).length}
          </span>
        )}

        {/* Unread messages badge - show total unread including group chat */}
        {(totalUnreadMessages > 0 || groupUnreadCount > 0) && (
          <span className="absolute -top-2 -left-2 bg-red-500 text-white text-xs rounded-full w-7 h-7 md:w-6 md:h-6 flex items-center justify-center font-bold animate-bounce">
            {totalUnreadMessages + groupUnreadCount}
          </span>
        )}

        {/* Missed calls badge */}
        {missedCallCount > 0 && (
          <span className="absolute -bottom-2 -left-2 bg-orange-500 text-white text-xs rounded-full w-7 h-7 md:w-6 md:h-6 flex items-center justify-center font-bold shadow-lg">
            <Phone className="w-4 h-4" />
          </span>
        )}
        </button>

        {/* Tooltip */}
        {!isExpanded && (
          <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
            Kullanıcılar
          </div>
        )}
      </div>

      {/* Message Modal */}
      <MessageModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        userId={selectedUserId}
        onStartVideoCall={onStartVideoCall}
      />

      {/* Group Chat Modal */}
      <GroupChatModal
        isOpen={showGroupChat}
        onClose={() => setShowGroupChat(false)}
        onStartVideoCall={onStartVideoCall}
      />
    </>
  );
});

export default OnlineUsersWidget;
